import { normalizeNumber, Permission, showConfirmAlert } from '_metronic/helpers'
import { useAuth } from '../../../../auth'

/**
 * Reprocessamento das faturas em aberto ao alterar o valor do profissional (LO-1373, backend LO-1355).
 *
 * Ao salvar um valor (tela de valores do profissional ou edição do funcionário), o usuário escolhe
 * se as faturas ainda na situação Gerada devem ser recalculadas com o valor novo; a escolha vira o
 * parâmetro de query `reprocessarFaturas=true`. "Não" continua salvando o valor — só não reprocessa.
 */

/**
 * Grupos que recebem `Financeiro:write` no seed do backend (`permission.json`). É a permissão que o
 * `PUT /funcionarios/{id}?reprocessarFaturas=true` exige além de `Funcionario:update`.
 *
 * LIMITAÇÃO CONHECIDA (a mesma de `_lotacaoPermissao.ts`): `/usuarios/me` não devolve `grupo.perfis`,
 * então o cliente só conhece o NOME DO GRUPO. O backend continua sendo a autoridade e devolve 403 em
 * qualquer divergência; aqui o gate só evita oferecer uma pergunta cuja resposta "sim" derrubaria a
 * gravação inteira do funcionário.
 */
export const GRUPOS_FINANCEIRO_ESCRITA: Permission[] = [
  Permission.SuperAdmin,
  Permission.SuperGerente,
]

/** Indica se o usuário autenticado pode pedir o reprocessamento ao editar o funcionário. */
export const usePermissaoReprocessarFaturas = (): boolean => {
  const { currentUser } = useAuth()
  return GRUPOS_FINANCEIRO_ESCRITA.includes(currentUser?.grupo?.nome as Permission)
}

type ValoresProfissional = { valorAtendimento?: unknown; valorRetorno?: unknown }

/**
 * Mudou o valor do atendimento ou do retorno? Compara pelo número: `''`, `null` e `undefined` são o
 * mesmo "sem valor", e `"150"` digitado é igual a `150` carregado.
 */
export const valoresProfissionalAlterados = (
  atual: ValoresProfissional,
  inicial: ValoresProfissional
): boolean =>
  normalizeNumber(atual.valorAtendimento) !== normalizeNumber(inicial.valorAtendimento) ||
  normalizeNumber(atual.valorRetorno) !== normalizeNumber(inicial.valorRetorno)

/**
 * Pergunta se as faturas Geradas devem ser recalculadas. Resolve `true` para reprocessar; `false`
 * (botão "Não") segue salvando sem o parâmetro.
 */
export const confirmarReprocessamentoFaturas = (): Promise<boolean> =>
  showConfirmAlert({
    title: 'Atualizar também as faturas em aberto?',
    text:
      'As faturas já geradas e ainda não autorizadas deste profissional serão recalculadas com o ' +
      'valor novo, inclusive sobre valores ajustados manualmente nelas. Faturas autorizadas, pagas, ' +
      'atrasadas ou canceladas não mudam.',
    icon: 'question',
    confirmButtonText: 'Sim, atualizar as faturas',
    cancelButtonText: 'Não, só salvar o valor',
  })
